"use client";

import { type ReactNode, createContext, useContext, useMemo } from "react";
import { type CountryCode } from "@/config/country-config";
import { type CountryRules, getCountryRules } from "@/config/country-rules";

interface CountryContextValue {
	country: CountryCode;
	rules: CountryRules;
	isCountry: (country: CountryCode) => boolean;
	isAnyCountry: (countries: readonly CountryCode[]) => boolean;
}

const CountryContext = createContext<CountryContextValue | null>(null);

interface CountryProviderProps {
	/** The request-resolved country, provided by a server component. */
	country: CountryCode;
	children: ReactNode;
}

/**
 * Provides the request-resolved country to client components.
 *
 * The synchronous `getCountry()` / `countryConfig` helpers can't read the dev
 * country cookie during SSR (no `document`), so in mock mode a client component
 * that branches on country during render produces different markup on the
 * server (env country) vs. the client (cookie country) — a hydration mismatch.
 * This provider is fed by the server-side `getRequestCountry()` (which is
 * cookie-aware), so client components read a single, consistent value on both
 * the server render and the client hydration.
 */
export const CountryProvider = ({
	country,
	children,
}: CountryProviderProps) => {
	const value = useMemo<CountryContextValue>(
		() => ({
			country,
			rules: getCountryRules(country),
			isCountry: (target) => country === target,
			isAnyCountry: (targets) => targets.includes(country),
		}),
		[country]
	);

	return (
		<CountryContext.Provider value={value}>{children}</CountryContext.Provider>
	);
};

const useCountryContext = (): CountryContextValue => {
	const context = useContext(CountryContext);
	if (!context) {
		throw new Error("useCountry must be used within a CountryProvider");
	}
	return context;
};

/** The request-resolved country (cookie-aware in mock mode, env otherwise). */
export const useCountry = (): CountryCode => useCountryContext().country;

/**
 * The request's country, its {@link CountryRules} and the `isCountry` /
 * `isAnyCountry` predicates, resolved per request via {@link CountryProvider}.
 */
export const useCountryConfig = (): CountryContextValue => useCountryContext();
